import { useEffect } from 'react';
import type { SendResult } from './sendApi';

// Ad Preview Stage 3 §3.1 / §3.2. Transient confirmation after a
// send is queued. Mirrors the fixed-bottom-right shell of
// BulkRejectionToast. Auto-dismisses after a few seconds; the AM can
// also dismiss manually. Sends process async, so the copy is
// deliberately "queued", not "sent".

type Props = {
  result: SendResult;
  onDismiss: () => void;
};

export function SendResultToast({ result, onDismiss }: Props) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const skipped = result.skippedChannels;
  const rejectedRows = result.rejectedRows ?? [];

  return (
    <div
      className="fixed bottom-6 right-6 z-40 w-[24rem] max-w-full rounded-md border border-emerald-300 bg-white p-4 shadow-xl"
      role="status"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm">
          <p className="font-medium text-slate-900">
            Queued {result.totalSends}{' '}
            {result.totalSends === 1 ? 'message' : 'messages'} to{' '}
            {result.totalRecipients}{' '}
            {result.totalRecipients === 1 ? 'recipient' : 'recipients'}.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Sending in the background — this may take a few minutes. Check the
            send log for delivery status.
          </p>
          {skipped.length > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              No eligible recipients for: {skipped.join(', ')}.
            </p>
          )}
          {rejectedRows.length > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              {rejectedRows.length} row{rejectedRows.length === 1 ? '' : 's'}{' '}
              skipped (no current preview link).
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="-mt-1 text-slate-400 hover:text-slate-700"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
